import React, { useRef, useState, useSyncExternalStore } from "react";
import { Popover, PopoverContent, PopoverTrigger, PopoverAnchor } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarBlank } from "@phosphor-icons/react";
import { dateFnsLocale } from "@/lib/dateLocale";
import { formatDate } from "@/lib/format";
import { pick as pickLang } from "@/lib/languages";
import { useSettings } from "@/context/SettingsContext";
import { searchFormL } from "@/i18n/search";
import { appShellL } from "@/i18n/appShell";
import { isNativeApp } from "@/lib/nativeApp";
import AppDatesSheet from "@/components/app/AppDatesSheet";

// The two date controls of the search form. Both are a button (or a pair of
// buttons) that opens a calendar in a popover and formats the chosen day in
// the site language; the range box is the one-panel version for trips with
// two ends. They own only their open/closed state — the dates live in
// SearchWidget, which passes them in and takes them back through onChange.

// A chosen day as the traveller reads it: "jeu. 23 oct.", with the year only
// when it is not this one ("dim. 3 janv. 2027"). No day yet: "Choisir une date".
export function dayLabel(date, lang) {
  if (!date) return "";
  return formatDate(date, { lang, year: date.getFullYear() !== new Date().getFullYear() });
}

// A phone shows one month: two stacked months are taller than the screen, and
// the panel opened above the top edge with the first weeks out of reach. The
// panel also stays inside the window whatever the field's place on the page
// (Radix measures the room left on each side), scrolling inside when even one
// month does not fit.
const WIDE = "(min-width: 640px)";
const subscribeWide = (onChange) => {
  if (typeof window === "undefined" || !window.matchMedia) return () => {};
  const query = window.matchMedia(WIDE);
  query.addEventListener?.("change", onChange);
  return () => query.removeEventListener?.("change", onChange);
};
const readWide = () => (typeof window !== "undefined" && window.matchMedia ? window.matchMedia(WIDE).matches : true);
export function useMonthsShown() {
  return useSyncExternalStore(subscribeWide, readWide, () => true) ? 2 : 1;
}
const PANEL = "w-auto max-w-[calc(100vw-16px)] p-0 max-h-[var(--radix-popover-content-available-height)] overflow-y-auto overscroll-contain";
const PANEL_PROPS = { align: "start", collisionPadding: 8, sticky: "always" };

const FIELD_LABEL = "block text-label-sm font-medium text-touki-muted";
// The apps draw the fields' icons in the regular weight (the approved design).
const iconWeight = () => (isNativeApp() ? "regular" : "bold");
const FIELD_VALUE = (chosen) => `block truncate text-body-md font-semibold tabular-nums ${chosen ? "text-touki-black" : "text-touki-muted"}`;

export function DateBox({ label, value, onChange, testId, minDate, compact = false }) {
  const { lang } = useSettings();
  const words = pickLang(searchFormL, lang);
  // A date field reads as a menu, not a panel: picking a day should dismiss it.
  // Clearing the selection passes undefined and keeps the calendar open, so the
  // user is never left with an empty field and no way to choose again.
  const [open, setOpen] = useState(false);
  const field = (
    <button type="button" data-testid={testId} aria-label={compact ? label : undefined} className={`w-full min-w-0 flex items-center gap-2 px-3 ${compact ? "min-h-[44px] rounded-md" : "min-h-[56px] rounded-lg"} bg-white border border-touki hover:border-touki-black transition-colors text-left`}>
      <CalendarBlank size={18} weight={iconWeight()} className="text-touki-muted shrink-0" aria-hidden="true" />
      <span className="min-w-0">
        {!compact && <span className={FIELD_LABEL}>{label}</span>}
        <span className={FIELD_VALUE(Boolean(value))}>{value ? dayLabel(value, lang) : words.chooseDate}</span>
      </span>
    </button>
  );
  // The apps choose the day in a sheet from the bottom of the screen.
  if (isNativeApp()) {
    return (
      <>
        {React.cloneElement(field, { onClick: () => setOpen(true), "aria-haspopup": "dialog" })}
        <AppDatesSheet open={open} onOpenChange={setOpen} title={label} mode="single" from={value} minDate={minDate}
          onConfirm={({ from }) => onChange(from)} testId={testId ? `${testId}-sheet` : "date-sheet"} />
      </>
    );
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {field}
      </PopoverTrigger>
      <PopoverContent className={PANEL} {...PANEL_PROPS}>
        <Calendar mode="single" selected={value} defaultMonth={value || minDate || new Date()} onSelect={(d) => { onChange(d); if (d) setOpen(false); }} disabled={minDate ? { before: minDate } : { before: new Date() }} locale={dateFnsLocale(lang)} initialFocus />
      </PopoverContent>
    </Popover>
  );
}

/**
 * Both dates of a trip, chosen in one calendar — the Hopper pattern.
 *
 * Two separate popovers made the traveller open a panel, pick, watch it close,
 * open a second panel and lose sight of the first date. One panel keeps the
 * whole stay visible: the nights in between are shaded, and moving either end
 * re-shades them immediately.
 *
 * The two halves share a single Popover anchored on the box, so whichever half
 * is clicked opens the same calendar — only the field the next click fills
 * changes. The panel closes when the range is complete, and stays open when
 * only the first end is set, because there is still something to pick.
 */
export function DateRangeBox({ labelFrom, labelTo, from, to, onChange, testIdFrom, testIdTo, minDate, sheetTitle, unit = "nights" }) {
  const { lang } = useSettings();
  const words = pickLang(searchFormL, lang);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState("from");
  const months = useMonthsShown();
  const fromRef = useRef(null);
  const toRef = useRef(null);
  const show = (d) => (d ? dayLabel(d, lang) : words.chooseDate);

  const openFor = (field) => { setEditing(field); setOpen(true); };

  // Radix restores focus to a Trigger; there is none here, so the button that
  // opened the panel takes it back by hand.
  const close = () => {
    setOpen(false);
    (editing === "to" ? toRef : fromRef).current?.focus();
  };

  const pick = (day) => {
    if (!day) return;
    if (editing === "to" && from && day >= from) {
      onChange({ from, to: day });
      close();
      return;
    }
    // Any click that is not a valid return starts the trip again. An existing
    // return survives only while it still falls after the new departure.
    const keptTo = to && to > day ? to : undefined;
    onChange({ from: day, to: keptTo });
    if (keptTo) { setOpen(false); fromRef.current?.focus(); } else { setEditing("to"); }
  };

  const half = "flex-1 min-w-0 flex items-center gap-2 px-3 min-h-[56px] text-left transition-colors hover:bg-touki-light";
  const box = (
    <div className="tk-search-daterange flex items-stretch rounded-lg bg-white border border-touki hover:border-touki-black transition-colors overflow-hidden min-w-0">
      <button type="button" ref={fromRef} onClick={() => openFor("from")} data-testid={testIdFrom} aria-haspopup="dialog" aria-expanded={open} className={half}>
        <CalendarBlank size={18} weight={iconWeight()} className="text-touki-muted shrink-0" aria-hidden="true" />
        <span className="min-w-0">
          <span className={FIELD_LABEL}>{labelFrom}</span>
          <span className={FIELD_VALUE(Boolean(from))}>{show(from)}</span>
        </span>
      </button>
      <span className="w-px bg-touki shrink-0" aria-hidden="true" />
      <button type="button" ref={toRef} onClick={() => openFor("to")} data-testid={testIdTo} aria-haspopup="dialog" aria-expanded={open} className={half}>
        <CalendarBlank size={18} weight={iconWeight()} className="text-touki-muted shrink-0" aria-hidden="true" />
        <span className="min-w-0">
          <span className={FIELD_LABEL}>{labelTo}</span>
          <span className={FIELD_VALUE(Boolean(to))}>{show(to)}</span>
        </span>
      </button>
    </div>
  );
  // The apps choose both dates in a sheet from the bottom of the screen, the
  // months one under the other, confirmed with "Valider".
  if (isNativeApp()) {
    return (
      <>
        {box}
        <AppDatesSheet open={open} onOpenChange={setOpen} title={sheetTitle || pickLang(appShellL, lang).sheets.dates}
          from={from} to={to} minDate={minDate} labelFrom={labelFrom} labelTo={labelTo} startWith={editing} unit={unit}
          onConfirm={(range) => onChange(range)} />
      </>
    );
  }
  return (
    <Popover open={open} onOpenChange={(v) => (v ? setOpen(true) : close())}>
      <PopoverAnchor asChild>
        {box}
      </PopoverAnchor>
      <PopoverContent className={PANEL} {...PANEL_PROPS}>
        <Calendar
          mode="range"
          numberOfMonths={months}
          selected={{ from, to }}
          defaultMonth={editing === "to" && from ? from : from || minDate || new Date()}
          onDayClick={pick}
          onSelect={() => {}}
          disabled={{ before: minDate || new Date() }}
          locale={dateFnsLocale(lang)}
          initialFocus
        />
        <p className="px-3 pb-3 -mt-1 text-xs text-touki-muted" data-testid="daterange-hint">
          {/* The hint names the field being filled, so the same panel reads
              correctly for a return flight and for a hotel checkout. */}
          {words.pickPrompt((editing === "to" && from ? labelTo : labelFrom).toLowerCase())}
        </p>
      </PopoverContent>
    </Popover>
  );
}
